// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMUrlUtilService } from '../services/utils/lm-url-util.service';
import {
  LMContextService,
  LMLoggerService,
  LMModalWrapperService,
  LMRouteComponentService,
  LMSpecService,
  LMUrlInfoBuilderService
} from '../services';
import { NavigationExtras, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, EMPTY,  firstValueFrom, Observable, of, Subscriber, Subscription, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { AppConstants, LMDialogButtonKey } from './app.constants';
import { SohoToastService } from 'ids-enterprise-ng';
import { intersectionWith } from 'lodash-es';
import { isEqual } from 'lodash-es';

import { LMGlobalPersonalization, LMGlobalPersonalizationService } from '@app/states';
import { LMManualRepresentationComponent, LMUrlDocSaveResult } from '@app/components';
import { LMBannerHeaderComponentRef } from '@banner/lm-banner-header-component-ref.service';
import { LiveRegionConfig } from '../../directives/lm-live-region.directive';

declare function lmParseScannableResults(parseValue: string): any;   // define the function that will be called for scannable field parsing

@Injectable()
export class LMGlobalUtilService {
public liveMessageToast: LiveRegionConfig = { liveMessage: '', useHostAsLiveRegion: true }
  constructor(
    private router: Router,
    private http: HttpClient,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmSpecService: LMSpecService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmRouteComponentService: LMRouteComponentService,
    private sohoToastService: SohoToastService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmModalWrapperService: LMModalWrapperService,
  ) {}

  public getNavContextKeys(params: any) {
    const navContextKeys: any = {};
    for (const key in params) {
      if (typeof key === 'string'
        && key.indexOf('nc.') === 0) {
        navContextKeys[ key ] = params[ key ];
      }
    }
    return navContextKeys;
  }

  public navigate(url: string, params: any = null, option: any = null) {
    if (this.lmUrlUtilService.isDrillBackUrlLink(url)) {
      this.lmUrlUtilService.openExternalUrl(url);
      return;
    }


    let openInNewTab: any;

    // todo/sjw xi: can remove this?
    // if (window.isSpa)
    // {
    //    if (url.indexOf("fragment=") < 0)
    //        url = this.insertFragmentParam(url);
    //
    //    this.loadContent(url, "Need to get", true)
    //    return;
    // }

    if (params) {
      url = this.lmUrlUtilService.appendToQueryString(url, this.lmUrlUtilService.objectToQueryString(params));
    }

    if (option === 'replace') {
      window.location.replace(url);
    } else {
      openInNewTab = option === 'external' || false;

      try {
        if (openInNewTab) {
          this.lmUrlUtilService.openExternalUrl(url);
        } else {
          window.location.href = url;
        }
      } catch (e) {

        // Catch errors in IE for the onbeforeunload event;
        // Jquery fails in IE when discarding the second event of a jQuery.event.trigger() and
        // when an event is called after a page has unloaded.

        // IE throws a JS error if "Stay on this page" is chosen in the confirmation dialog.
        // if (this.browserUtil.isIE && e.message.length === 0 && e.description.length === 0)
        //     return;
        //
        // this.dialogUtil.showErrorDialog("Error Opening URL", e);
      }
    }
  }

  public compare(obj1: Object, obj2: Object): boolean {

    // Create arrays of property names
    const props1 = Object.getOwnPropertyNames(obj1);
    const props2 = Object.getOwnPropertyNames(obj2);

    // If number of properties is different objects are not equivalent
    if (props1.length !== props2.length) {
      return false;
    }

    for (let i = 0; i < props1.length; i++) {
      const propName = props1[i];

      // If values of same property are not equal, objects are not equivalent
      if (obj1[propName] !== obj2[propName]) {
        return false;
      }
    }

    // If we made it this far, objects are considered equivalent
    return true;
  }

  private getHashCompareUrl(urlIn: string): string {
    if (this.lmUrlUtilService.isAbsoluteUrl(urlIn)) {
      // first check to see if it has the protocol and remove it
      urlIn = this.lmUrlUtilService.toRelativeUrl(urlIn);
    }
    return urlIn.split('#')[0];
    // const protocolStriped = (urlIn.startsWith('http://') || urlIn.startsWith('https://')) ? urlIn.substring((urlIn.indexOf('//') + 2)) : urlIn;
    // const pathUrl: string = protocolStriped.indexOf('#') >= 0 ? protocolStriped.split('#')[0] : protocolStriped;
    // return pathUrl;
  }

  /**
   * prepares the url and routes to it
   * @param url The url to route to.
   * todo placed here because of circular dependency issues.
   */
  public async navigateByUrl(url: string, extras?: NavigationExtras): Promise<boolean> {

    // if the path is the same and the url has a # then instead of allowing the nevigation to the url we fire an HashChangeEvent.
    const urlAndHash = url.split('#');
    if (urlAndHash.length === 2 // hash exist on url
    &&  this.lmUrlUtilService.isPageUrl(url)
    &&  this.lmUrlUtilService.isPageUrl(this.router.url)
    &&  this.pageCurrentAndNavigationUrlsMatch(this.router.url, url)) {
      const comp = this.lmRouteComponentService.getRouteComponent();
      if (comp['hashTagChanged']) {
        if (comp['hashTagChanged'](urlAndHash[0], urlAndHash[1])) {
          return Promise.resolve(true);
        }
      }
    }

    if (url.indexOf('/menu/') >= 0) {
      const response = await firstValueFrom(this.lmSpecService.getApplicationMenuItemSpec(url).pipe(
        catchError(error => {
          this.lmLoggerService.log('Unable to find menu');
          return EMPTY;
        })
      ));

      if (response?.redirectUrl) {
        if (response.external) {
          this.lmUrlUtilService.openExternalUrl(response.redirectUrl);
        } else if (response.embedded) {

          if (url.length > 0) {
            const isHtmlFragment = response.redirectUrl.indexOf('standalone=true') < 0;
            const webapp = this.lmContextService.getContext().webApp;
            const dataArea = this.lmContextService.getContext().urlPdlOrDa;
            let autoLoadUrl = dataArea + '/' + webapp + '/html/url?autoLoadUrl=' + encodeURIComponent(response.redirectUrl);
            autoLoadUrl = this.lmUrlUtilService.updateContextSessionKeyParams(autoLoadUrl);
            return this.navigateByUrl(autoLoadUrl, extras);
          }
        } else {
          const redirectUrl = this.lmUrlUtilService.updateContextSessionKeyParams(response.redirectUrl);
          const noDefaultPortUrl = this.lmUrlUtilService.stripDefaultPort(redirectUrl);
          // Menu items from a unified application is a drillback URL. It goes to the URL in the plugin definition first
          // before taking the user to drillback URL. Set the accessible webapps correctly here
          if (response.webApps && this.lmContextService.isUnified()) {
            // In the case of HRT, this is set to EmployeeSelfService by the app initializer in app.component
            // Reset it based on the webapp the menu item is navigating to.
            this.lmContextService.setAccessibleWebApps(response.webApps);
            this.lmContextService.updateWebApp(response.webApps[0]);
          }
          return this.navigateByUrl(noDefaultPortUrl, extras).then((completed?: boolean) => {
            this.lmLoggerService.log('navigationByUrl completed?: ' + completed);
            return completed;
          });
        }
      }
      return Promise.resolve(true);
    }

    let relativeUrl = this.lmUrlUtilService.toRelativeUrl(url);
    relativeUrl = this.lmUrlUtilService.encodePlus(relativeUrl);
    relativeUrl = this.lmUrlUtilService.encodeParens(relativeUrl);

    // ---------------------------------------------------------------------------
    // todo ppatton if navigating to a new route (form) then apply paging
    // information to the url so the form next/prev functionality works.
    // ---------------------------------------------------------------------------
    return this.router.navigateByUrl(relativeUrl, extras);
  }

  public updateClientStatsTelegraf(info: LMComponentInfo, loadTime: number): Observable<any> {
    if (!this.lmContextService.isMonocleEnabled()) {
      return of();
    }

    const telegrafProperties: LMTelegrafProperties = this.getUrlRequestInfoJson(info, loadTime, true, true);
    return this.sendClientStatsTelegraf(telegrafProperties, false);
  }

  // use this to send information to Telegraf
  public sendClientStatsTelegraf(telegrafProperties: LMTelegrafProperties, custom: boolean, name?: string): Observable<any>  {

    this.lmLoggerService.log([ this, this.lmLoggerService.instanceId, 'updateClientStatsTelegraf' ]);
    telegrafProperties['custom'] = custom;
    telegrafProperties['customName'] = name;

    const postContent: any = {
      requestType:    'telegraf',
      telegrafProperties
    };

    return this.http.post(this.lmUrlUtilService.getTelegrafUrl(), postContent).pipe(
      catchError(error => throwError(error || 'Server error')));
  }

  // used to cancel long running calls that the user has decided not to wait for nd hit the cancel button
  public sendInterruptThreads(headers: HttpHeaders): Observable<any>  {

    if (this.lmContextService.isAnonymousUser() || this.lmContextService.isGuestUser()) {
      this.lmLoggerService.log('Anonoymous or Guest user attempted to Interrupt a thread');
      return;
    }

    this.lmLoggerService.log([ this, this.lmLoggerService.instanceId, 'sendInterruptThreads' ]);

    const postContent: any = {
      requestType:    'interruptThreads',
    };

    return this.http.post(this.lmUrlUtilService.getInteruptThreadUrl(), postContent, { headers}).pipe(
      catchError(error => throwError(error || 'Server error')));
  }

  public getUrlRequestInfoJson(info: LMComponentInfo, loadTime: number, configFlags?: boolean, userPageHit?: boolean): TelegrafProperties {
    const context: LMContext = this.lmContextService.getContext();
    // const sharePageAnalyticParts: string[] = context ? context.sharePageAnalytics.split('_') : [];
    // const accountId: string = sharePageAnalyticParts.length > 0 ? sharePageAnalyticParts[ 0 ] : '';
    // const application: string = sharePageAnalyticParts.length > 1 ? sharePageAnalyticParts[ 1 ] : 'Improper_sharePageAnalytic';
    // const environmentType: string = sharePageAnalyticParts.length > 2 ? sharePageAnalyticParts[ 2 ] : 'Improper_sharePageAnalytic';
    // // let telegrafUrl = this.lmUrlUtilService.getTelegrafUrl();
    const urlSplit = this.router.url.split('?');

    const  telegrafProperties: TelegrafProperties = {
  //    accountId,
      actorUniqueId: context ? context.actorUniqueId : '',
      webApp: context ? context.webApp : '',
      userWebRequestId: info ? info.userWebRequestId : '',
  //    application,
  //    environmentType,
      isoLocale: Soho.Locale.currentLocale.name,
      userAgent: navigator.userAgent,
      businessClass: info ? info.busClassName : '',
      uiView: info ? info.name : '',
      uiViewType: info ? info.componentType : this.getUIType(urlSplit[0]),
      displayType: info ? info.displayType : '',
      tenant: info ? info.tenant : '',
      envType: info ? info.envType : '',
      module: info ? info.module : '',
      message: info ? info.message : '',
      theme: info ? info.theme : '',
      color: info ? info.color : '',
      menuVersion: info ? info.menuVersion : '',
      routeReuse: info ? info.routeReuse : false,
    };

    if (userPageHit) {
      telegrafProperties.url = this.router.url;
      telegrafProperties.urlParams = urlSplit.length > 1 ? this.filterAllowableParams(this.router.url) : '';
    }

    if (configFlags) {
      telegrafProperties['isReport'] = (info && info.configInfo && (info.configInfo.isReport || info.configInfo.folder));
      telegrafProperties['loadTime'] = loadTime ? loadTime : -1;
      telegrafProperties['developer'] = info ? info.developer : false;
      telegrafProperties['isConfigured'] = info && info.configInfo ? info.configInfo.configuration : false;
      telegrafProperties['isPersonalized'] = info && info.configInfo ? info.configInfo.personalization : false;
      telegrafProperties['userFolder'] = info && info.configInfo ? info.configInfo.userlist : false;
    }

    return telegrafProperties;
  }

  private filterAllowableParams(url: string): string {
    const approvedArray: Array<string> = [ 'action', 'csk.showusingxi', 'csk.ng', 'csk.mi', 'csk.CHP', 'csk.hidemenubar', 'csk.hidetitlebar', 'csk.developer',
      'csk.dbsessiondebug', 'csk.gen', 'csk.3x', 'csk.mw' ];

    let filteredParams = '';
    const urlParams = this.lmUrlUtilService. getUrlParams(url);
    const paramArray = Object.keys(urlParams);
    const overlap = intersectionWith(approvedArray, paramArray, isEqual);
    overlap.map(key => {
      filteredParams += '&' + key + '=' + urlParams[key];
    });

    return filteredParams;
  }

  private getUIType(url: string): string {
    if (url.indexOf('/home/') > -1) {
      return 'HOME';
    } else if (url.indexOf('/page/') > -1) {
      return 'PAGE';
    }

    return '';
  }

  private pageCurrentAndNavigationUrlsMatch(currentUrl: string, navigationUrl: string): boolean {
    const fromUrl: string = this.getHashCompareUrl(currentUrl.toLowerCase());
    const toUrl: string = this.getHashCompareUrl(navigationUrl);
    const fromUrlInfo = this.lmUrlInfoBuilderService.getPageUrlInfoFromHttpUrl(fromUrl);
    const toUrlInfo = this.lmUrlInfoBuilderService.getPageUrlInfoFromHttpUrl(toUrl);

    // ---------------------------------------------------------------------------
    // if the resourceIds do not match then urls do not match
    // ---------------------------------------------------------------------------
    if (fromUrlInfo.resource.resourceId !== toUrlInfo.resource.resourceId) {
      return false;
    }

    // ---------------------------------------------------------------------------
    // if no sourceResourceIds then the urls match
    // ---------------------------------------------------------------------------
    if (!fromUrlInfo.sourceResource && !toUrlInfo.sourceResource) {
      return true;
    }

    // ---------------------------------------------------------------------------
    // if there are sourceResources then if they are navigation or drill the
    // resourceId must match
    // ---------------------------------------------------------------------------
    if (fromUrlInfo.sourceResource && toUrlInfo.sourceResource) {
      if (fromUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_NAVIGATION
      &&  toUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_NAVIGATION) {
        return fromUrlInfo.sourceResource.resourceId === toUrlInfo.sourceResource.resourceId;
      }

      if (fromUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_DRILL
      &&  toUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_DRILL) {
        return fromUrlInfo.sourceResource.resourceId === toUrlInfo.sourceResource.resourceId;
      }
    }

    // ---------------------------------------------------------------------------
    // if either or both have a sourceResourceTypes are 'menu' then it ok to
    // ignore that and consider this a match
    // ---------------------------------------------------------------------------
    if ((fromUrlInfo.sourceResource && fromUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_MENU)
    ||  (toUrlInfo.sourceResource && toUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_MENU)) {
      return true;
    }

    return false;
  }

  public displayToastMessage(title: string, message: string, resultAttributeValue?: string, allowLink?: boolean) {
    const toastIDAttributes: SohoAttributesArray = [];
    toastIDAttributes.push({ name: 'data-automation-id', value: 'lm-toast-message-container' });
    if (resultAttributeValue?.trim().length > 0) {
      toastIDAttributes.push({ name: 'toast-message-result', value: resultAttributeValue.trim() });
    }

    const position = this.getToastMessagePosition();
    const duration = this.getToastMessageDuration();

    const toastOptions = {
      title,
      message,
      position,
      allowLink: allowLink || false,
      draggable: true,
      savePosition: true,
      progressBar: false,
      timeout: duration, // 30000, // for debugging
      attributes: toastIDAttributes,
      uniqueid: (this.lmContextService.getActorUniqueId() === null) ? 'message' : this.lmContextService.getActorUniqueId()
    };
    // when passing in uniqueid soho will append toast- -usersettings-position to localStorage item
    this.sohoToastService.show(toastOptions);
    //assign liveMessageToast
    if (message) this.liveMessageToast = { ...this.liveMessageToast, liveMessage: message };
  }

  public getToastMessagePosition(): SohoToastPosition {
    const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    const configParam = this.lmContextService.getToastMessagePosition();
    let position = null;

    if (bannerPersonalization.toastMessagePosition && bannerPersonalization.toastMessagePosition !== 'DEFAULT') {
      position = bannerPersonalization.toastMessagePosition;
    } else if (configParam) {
      position = configParam;
    } else {
      return SohoToastService.TOP_RIGHT;
    }

    if (position === 'TOP_LEFT') {
      return SohoToastService.TOP_LEFT;
    } else if (position === 'TOP_RIGHT') {
      return SohoToastService.TOP_RIGHT;
    } else if (position === 'BOTTOM_LEFT') {
      return SohoToastService.BOTTOM_LEFT;
    } else if (position === 'BOTTOM_RIGHT') {
      return SohoToastService.BOTTOM_RIGHT;
    } else {
      return SohoToastService.TOP_RIGHT;
    }
  }

  public getToastMessageDuration(): number {
    const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    const configParam = this.lmContextService.getToastMessageDuration();
    let duration = null;

    if (bannerPersonalization.toastMessageDuration) {
      duration = bannerPersonalization.toastMessageDuration;
    } else if (configParam) {
      duration = configParam;
    } else {
      return 6000;
    }

    if (duration === 'SHORTER') {
      return 4000;
    } else if (duration === 'DEFAULT') {
      return 6000;
    } else if (duration === 'LONGER') {
      return 8000;
    } else {
      return 6000;
    }
  }

  public displayActionNotAllowed() {
    const dialogId = 'ActionNotAllowed';
    const messageDialog = this.lmModalWrapperService
      .message()
      .message(Soho.Locale.translate('ActionNotAllowedDetails'))
      .title(Soho.Locale.translate('ActionNotAllowed'))
      .buttons([
        {
          id: dialogId + '_' + LMDialogButtonKey.CLOSE,
          text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
          click: (e, modal) => {
            modal.close(true);
          }
        },
      ])
      .open();
  }

  public loadScannableJsLib() {
    const url = this.lmUrlUtilService.getBaseUrl() + '/js/scannableparse.js';
    this.loadDynamicJsLib(url);
  }

  public loadDynamicJsLib(scriptUrl: string) {
    const chatScript = document.createElement('script');
    chatScript.type = 'text/javascript';
    chatScript.async = true;
    chatScript.src = scriptUrl;
    document.body.appendChild(chatScript);
    this.lmLoggerService.log('An attempt was made to dynamically load the library "' + scriptUrl + '." It is possible for it to not be found and not be considered an error.');
  }

  // if the external js library is not found the callScannableParse will return null
  public callScannableParse(scannedValue: string, specBindId?: string): LMModelFieldInfo {
    const fieldData: LMModelFieldInfo = {};
    try {
      const scannableReturn = lmParseScannableResults ? lmParseScannableResults(scannedValue) : null ;
      if (scannableReturn === null) {
        return null;
      }

      if (typeof scannableReturn === 'string') {
        if (specBindId) {
          fieldData[specBindId] = scannableReturn;
        } else {
          return null;
        }
      } else {
        for (const retVal of scannableReturn) {
          const fieldInfo = retVal.split(':');
          if (fieldInfo.length === 2) {
            fieldData[fieldInfo[0]] = fieldInfo[1];
          } else {
            this.lmLoggerService.log('unable to process scannable parse return. Name/Value pair structure is incorrect.');
          }
        }
      }
    } catch (e) {
      return null;
    }

    return fieldData;
  }

  public runSaveManualReps(observer: Subscriber<boolean>, manualRefComps: LMManualRepresentationComponent[]) {
    // process here should be to try to save the manual reps first and if they do not save properly do not save the form.
    //  const refs: LMManualRepresentationComponent[] = this.lmHtmlUrlDocService.getManualRefCompRefs();
    const manualRepSaves = [];
    for(const comp of manualRefComps) {
      const manRepObservable: BehaviorSubject<LMUrlDocSaveResult> = comp.runSave();
      manualRepSaves.push(manRepObservable);
    }
    const combineSubscrption = combineLatest(manualRepSaves).subscribe(
      res => {
        const hasErrors = res.filter( (saved: LMUrlDocSaveResult ) => saved.isError === true);
        const complete = res.filter( (saved: LMUrlDocSaveResult ) => saved.isComplete !== true);
        if (hasErrors.length) {
          this.displayToastMessage('', (hasErrors[0] as LMUrlDocSaveResult).message, 'unknown');
          observer.next(false);
          observer.complete();
          this.cleanupManualRepSave(combineSubscrption, manualRepSaves);
        } else if (complete.length) {
          // got no errors but not all are complete so keep going
        } else {
          observer.next(true);
          observer.complete();
          this.cleanupManualRepSave(combineSubscrption, manualRepSaves);
        }
      },
    );
  }

  cleanupManualRepSave(combineSubscrption: Subscription, res: any[]) {
    combineSubscrption.unsubscribe();
    res.map( (item: BehaviorSubject<LMUrlDocSaveResult>) => item.complete());
  }
}
